<script setup>
import {onMounted, ref} from "vue";
import FormSection from "../../../../elements/forms/FormSection.vue";
import Btn from "../../../../elements/Btn.vue";
import TextField from "../../../../elements/forms/TextField.vue";
import ValidationErrors from "../../../common/ValidationErrors.vue";
import Drawer from "../../../../elements/Drawer.vue";

const props = defineProps({
        program: {
            type: Object,
            required: true
        },
        loggedAccount: {
            type: Object,
            default: ''
        },
        recentAiCalls: {
            type: Object,
            default: () => []
        },
        affiliateDiscoveryCtaClicked: {
            type: Boolean,
            default: false
        }
    });

    const websiteUrl = ref('')
    const productDescription = ref('')
    const autoGeneratingDescription = ref(false);

    const apiAiError = ref(null);

    const newKeyword = ref('');
    const keywords = ref([]);

    const findingCompetitors = ref(false);
    const competitors = ref([]);
    const newCompetitorName = ref('');
    const newCompetitorDomain = ref('');

    const discoveredAffiliates = ref([]);
    const discoveringAffiliates = ref(false);

    const showInterveneDrawer = ref(false);

    const productDetailsRef = ref(null);
    const keywordsRef = ref(null);
    const findCompetitorsRef = ref(null);



    const resetData = () => {
        websiteUrl.value = '';
        productDescription.value = '';
        keywords.value = [];
        competitors.value = [];
        discoveredAffiliates.value = [];
        apiAiError.value = null;
    };

    const getSuggestedMessage = (affiliate) => {
        return `Hi ${affiliate.name},

We discovered your platform while researching top communities for our audience interested in ${keywords.value.join(', ')}.
We'd love to explore a potential partnership or collaboration with you.

Looking forward to hearing from you.
\nBest regards,
\n${props.loggedAccount.name || props.program.name},
${props.loggedAccount.website || props.program.landing_page_url}`;
    };

    onMounted(() => {
        const aiCalls = props.recentAiCalls || {};

        // We start from the most complete step and work backwards to prefill fields
        if (aiCalls.discover_affiliates) {
            const input = aiCalls.discover_affiliates.user_input || {};
            websiteUrl.value = input.website_url || '';
            productDescription.value = input.description || '';
            keywords.value = input.keywords || [];
            competitors.value = input.competitors || [];
            try {
                discoveredAffiliates.value = JSON.parse(aiCalls.discover_affiliates.message || '[]');
            } catch (e) {
                discoveredAffiliates.value = [];
            }
        }

        else if (aiCalls.find_competitors) {
            const input = aiCalls.find_competitors.user_input || {};
            websiteUrl.value = input.website_url || '';
            productDescription.value = input.description || '';
            keywords.value = input.keywords || [];
            try {
                competitors.value = JSON.parse(aiCalls.find_competitors.message || '[]');
            } catch (e) {
                competitors.value = [];
            }
        }

        else if (aiCalls.generate_product_description) {
            const input = aiCalls.generate_product_description.user_input || {};
            websiteUrl.value = input.website_url || '';
            productDescription.value = aiCalls.generate_product_description.message || '';
        }
    });

    const addKeyword = () => {
        const keyword = newKeyword.value.trim();
        if (keyword && !keywords.value.includes(keyword)) {
            keywords.value.push(keyword);
            newKeyword.value = '';
        }
    };

    const addCompetitor = () => {
        const name = newCompetitorName.value.trim();
        const domain = newCompetitorDomain.value.trim();
        if (name && domain && !competitors.value.some(c => c.name === name && c.domain === domain)) {
            competitors.value.push({ name, domain });
            newCompetitorName.value = '';
            newCompetitorDomain.value = '';
        }
    };

    const autoGenerateDescription = () => {

        apiAiError.value = null;
        autoGeneratingDescription.value = true;

        axios.post(route('app_int.ai.generate_product_description', {program: props.program}), {
            website_url: websiteUrl.value
        })
            .then(response => {
                const status = parseInt(response.data.status || -1);
                if (status === 1) {
                    productDescription.value = response.data.description;
                    productDetailsRef.value?.collapse();
                }

                else {
                    apiAiError.value = response.data.message || 'An error occurred while generating the description.';
                }
            })
            .catch( error => {
                apiAiError.value = error.response?.data?.message || 'An error occurred while generating the description.';
            })
            .finally(() => {
                autoGeneratingDescription.value = false;
            });
    };

    const findCompetitors = () => {

        apiAiError.value = null;
        findingCompetitors.value = true;

        axios.post(route('app_int.ai.find_competitors', {program: props.program}), {
            keywords: keywords.value,
            description: productDescription.value,
            website_url: websiteUrl.value
        })
            .then(response => {
                const status = parseInt(response.data.status || -1);
                if (status === 1) {
                    competitors.value = response.data.competitors || [];
                    findingCompetitors.value = false;
                    keywordsRef.value?.collapse();
                }

                else {
                    apiAiError.value = response.data.message || 'An error occurred while generating the description.';
                }
            })
            .catch( error => {
                apiAiError.value = error.response?.data?.message || 'An error occurred while generating the description.';
            })
            .finally(() => {
                findingCompetitors.value = false;
            });
    };

    const discoverAffiliates = () => {

        apiAiError.value = null;
        discoveringAffiliates.value = true;

        axios.post(route('app_int.ai.discover_affiliates', {program: props.program}), {
            competitors: competitors.value,
            keywords: keywords.value,
            description: productDescription.value,
            website_url: websiteUrl.value
        })
            .then(response => {
                const status = parseInt(response.data.status || -1);
                if (status === 1) {
                    discoveringAffiliates.value = false;
                    discoveredAffiliates.value = response.data.affiliates || [];
                    if (discoveredAffiliates.value.length > 0) {
                        productDetailsRef.value?.collapse();
                        keywordsRef.value?.collapse();
                        findCompetitorsRef.value?.collapse();
                    }
                }

                else {
                    apiAiError.value = response.data.message || 'An error occurred while discovering affiliates.';
                }
            })
            .catch( error => {
                apiAiError.value = error.response?.data?.message || 'An error occurred while discovering affiliates.';
            })
            .finally(() => {
                discoveringAffiliates.value = false;
            });
    };

    const shouldShowCta = ref(!props.recentAiCalls.discover_affiliates && !props.recentAiCalls.find_competitors && !props.recentAiCalls.generate_product_description);
    const handlingCtaClicked = ref(false);

</script>

<template>

    <div v-if="shouldShowCta" class="bg-white rounded-lg py-20 px-10 border flex justify-center ">

        <div class="max-w-lg text-center">
        <validation-errors :errors="apiAiError" class="mb-4" />

        <div class="flex justify-center items-center p-4">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24">
                <path d="M20.4668 8.69379L20.7134 8.12811C21.1529 7.11947 21.9445 6.31641 22.9323 5.87708L23.6919 5.53922C24.1027 5.35653 24.1027 4.75881 23.6919 4.57612L22.9748 4.25714C21.9616 3.80651 21.1558 2.97373 20.7238 1.93083L20.4706 1.31953C20.2942 0.893489 19.7058 0.893489 19.5293 1.31953L19.2761 1.93083C18.8442 2.97373 18.0384 3.80651 17.0252 4.25714L16.308 4.57612C15.8973 4.75881 15.8973 5.35653 16.308 5.53922L17.0677 5.87708C18.0555 6.31641 18.8471 7.11947 19.2866 8.12811L19.5331 8.69379C19.7136 9.10792 20.2864 9.10792 20.4668 8.69379ZM5.79993 16H7.95399L8.55399 14.5H11.4459L12.0459 16H14.1999L10.9999 8H8.99993L5.79993 16ZM9.99993 10.8852L10.6459 12.5H9.35399L9.99993 10.8852ZM15 16V8H17V16H15ZM3 3C2.44772 3 2 3.44772 2 4V20C2 20.5523 2.44772 21 3 21H21C21.5523 21 22 20.5523 22 20V11H20V19H4V5H14V3H3Z"></path>
            </svg>

        </div>
        <h2 class="font-semibold text-lg mb-2">Discover Affiliates with AI</h2>
        <p class="text-gray-500 text-sm text-center">
            Let AI scan your niche, analyze competitors, and uncover the best websites for affiliate outreach, tailored to your product.
        </p>
        <btn
            @click="shouldShowCta = false;"
            class="mt-8"
            size="large"
            :loading="handlingCtaClicked"
            :disabled="handlingCtaClicked"
        >
            {{ handlingCtaClicked ? 'Processing...' : 'Discover' }}
        </btn>
            </div>
    </div>

    <div v-else class="border rounded">

        <validation-errors :errors="apiAiError" class="px-10 pt-10 lg:pt-14" />

        <div class="px-10 divide-y divide-gray-200">
            <form-section ref="productDetailsRef"
                          title="1. Product details"
                          description="Provide basic information about your website and product to help us identify the most relevant affiliate partners."
                          show-collapse-button
            >
                <text-field
                    label="Website URL"
                    placeholder="E.g. partnero.com"
                    name="website_url"
                    type="text"
                    v-model="websiteUrl"
                    required="true"
                >
                </text-field>

                <div>
                    <label class="block text-sm leading-5 mb-1.5" for="product-description">
                        Product description
                    </label>
                    <textarea v-model="productDescription" id="product-description" rows="5"
                              class="block w-full text-sm rounded border-gray-300 focus:ring-0 focus:border-gray-400 mb-1 scroll-h"
                    ></textarea>
                    <small class="text-gray-500 mt-1 block">
                        Provide a brief description of your product or service. This will help us match you with the right affiliates.
                    </small>
                </div>

                <div class="bg-gradient-to-tr from-white via-gray-50 to-teal-50 border rounded p-4 text-sm">
                    <div class="font-medium mb-1">
                        Tip for better results
                    </div>
                    <p>The more specific your description, the more accurate and relevant our recommendations will be. To enhance your outcome, consider including:</p>
                    <ul class="list-disc list-inside mt-2">
                        <li>Core features and key benefits of your product or service</li>
                        <li>Target audience and relevant customer segments</li>
                        <li>Industry focus and typical use cases</li>
                    </ul>
                </div>

                <div class="mt-3">
                    <div class="text-sm mb-1.5">Not sure how to start?</div>
                    <btn
                        @click="autoGenerateDescription"
                        :disabled="!websiteUrl.trim() || autoGeneratingDescription"
                        :loading="autoGeneratingDescription"
                        type="primary"
                    >
                        {{ autoGeneratingDescription ? 'Analyzing website...' : 'Let us analyze your website' }}
                    </btn>
                </div>

            </form-section>
            <form-section v-if="productDescription"
                          ref="keywordsRef"
                          title="2. Keywords"
                          description="Add keywords your ideal customers might search for when looking for solutions like yours."
                          show-collapse-button
            >
                <div>
                    <text-field
                        label="Keyword"
                        name="new_keyword"
                        type="text"
                        v-model="newKeyword"
                        placeholder="Add a keyword (e.g. 'email marketing', 'SEO tools')"
                        required="true"
                        @keyup.enter="addKeyword"
                    >
                        <template #note>Enter keyword and hit enter or press the button to add it.</template>
                        <template #append>
                            <div class="pl-2 whitespace-nowrap">
                                <btn
                                    @click.prevent="addKeyword"
                                    :disabled="!newKeyword.trim() || keywords.includes(newKeyword.trim())"
                                >
                                    Add keyword
                                </btn>
                            </div>
                        </template>
                    </text-field>
                </div>
                <div class="flex flex-wrap gap-4 mt-5">
                    <template v-if="keywords.length > 0">
                        <div
                            v-for="keyword in keywords"
                            :key="keyword"
                        >
                            <btn
                                class="flex-shrink-0 rounded-full"
                                type="secondary"
                                icon-name="close"
                                :icon-right="true"
                                size="small"
                                :custom-size="{ iconSize: 10, size: 'default' }"
                                @click.prevent="keywords.splice(keywords.indexOf(keyword), 1)"
                            >
                                {{ keyword }}
                            </btn>
                        </div>
                    </template>

                    <div
                        v-else
                        class="border rounded w-full p-6 text-sm text-gray-500 text-center"
                    >
                        No keywords yet.
                    </div>
                </div>
            </form-section>
            <form-section
                    v-if="productDescription"
                    ref="findCompetitorsRef"
                    title="3. Competitors"
                    description="List competitors to help us find affiliates who are already talking about similar products."
                    show-collapse-button
            >
                <div class="grid md:grid-cols-3 gap-2">
                    <text-field
                        label="Competitor name"
                        name="new_competitor_name"
                        type="text"
                        v-model="newCompetitorName"
                        placeholder="e.g. 'Mailchimp'"
                        required="true"
                    >
                    </text-field>
                    <text-field
                        class="md:col-span-2"
                        label="Competitor domain"
                        name="new_competitor_domain"
                        type="text"
                        v-model="newCompetitorDomain"
                        placeholder="e.g. 'mailchimp.com'"
                        required="true"
                        @keyup.enter="addCompetitor"
                    >
                        <template #append>
                            <div class="pl-2 whitespace-nowrap">
                                <btn
                                    @click.prevent="addCompetitor"
                                    :disabled="!newCompetitorName.trim() || !newCompetitorDomain.trim() || competitors.some(c => c.name === newCompetitorName && c.domain === newCompetitorDomain)"
                                >
                                    Add competitor
                                </btn>
                            </div>
                        </template>
                    </text-field>
                </div>
                <div class="flex items-center gap-4 mb-4">
                    <div v-if="competitors.length > 0" class="grid grid-cols-1 md:grid-cols-2 gap-4 w-full">
                        <div v-for="(competitor, index) in competitors" :key="index" class="relative bg-white border rounded-lg p-4 shadow-sm">
                            <button @click="competitors.splice(index, 1)" class="absolute top-2 right-2 text-gray-400 hover:text-cyan-500">
                                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                            <div class="flex flex-col space-y-2">
                                <div class="font-medium text-gray-900 text-lg">
                                    {{ competitor.name }}
                                </div>

                                <a :href="`https://${competitor.domain}`" target="_blank" class="text-gray-500 text-sm">
                                    <div class="flex">
                                        {{ competitor.domain }}
                                        <icon class="ml-2" name="arrow-right-up"></icon>
                                    </div>
                                </a>
                            </div>
                        </div>
                    </div>
                    <div v-else class="border rounded w-full p-6 text-sm text-gray-500 text-center">
                        No competitors found or listed yet.
                    </div>
                </div>

                <div class="mt-3" v-if="keywords.length > 0">
                    <div class="text-sm mb-1.5">Looking for insight? We’ll identify your key competitors based on your keywords.</div>
                    <btn
                        @click.prevent="findCompetitors"
                        type="primary"
                        :loading="findingCompetitors"
                        :disabled="findingCompetitors"
                    >
                        {{ findingCompetitors ? 'Looking for competitors...' : 'Find competitors' }}
                    </btn>
                </div>

            </form-section>
            <div v-if="discoveredAffiliates.length">
                <div class="text-sm mt-10">
                    <alert type="success" class="text-gray-900">Based on your criteria, we’ve gathered a list of relevant resources, platforms, and affiliates where you can promote your product or service.</alert>
                </div>
                <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4 py-10">
                    <div v-for="(affiliate, index) in discoveredAffiliates" :key="index" class="relative bg-gradient-to-tr from-white via-gray-50 to-teal-50 border rounded-lg p-4">

                        <div class="sm:grid grid-cols-3">
                            <div class="col-span-2">
                                <div class="font-medium text-lg text-gray-900 mb-1">
                                    {{ affiliate.name }}
                                </div>

                                <a :href="`https://${affiliate.website}`" target="_blank" class="text-teal-600 hover:underline text-sm flex items-center mb-2">
                                    {{ affiliate.website.length > 30 ? affiliate.website.slice(0, 30) + '...' : affiliate.website }}
                                    <icon class="ml-1" name="arrow-right-up"></icon>
                                </a>

                                <div class="inline-block bg-gray-100 text-gray-600 text-xs px-2 py-1 rounded mb-2">
                                    {{ affiliate.type }}
                                </div>
                            </div>
                            <div class="flex sm:justify-end my-3 sm:my-0">
                                <drawer
                                    v-model:show="showInterveneDrawer"
                                    name="intervene-drawer"
                                    title="Contact suggestion"
                                    size="small"
                                    :show-footer="false"
                                >
                                    <template #toggle>
                                        <btn
                                            type="secondary"
                                            size="small"
                                            @click="showInterveneDrawer = true"
                                        >
                                            Intervene
                                        </btn>
                                    </template>
                                    <template #body>
                                        <div class="mt-5">
                                            <h3 class="text-lg font-semibold mb-2">{{ affiliate.name }}</h3>
                                            <div v-if="affiliate.contact">
                                                <label class="block mb-1 text-sm font-medium text-gray-700">
                                                    Suggested contact (please verify):
                                                </label>
                                                <a v-if="affiliate.contact?.startsWith('http')"
                                                   :href="affiliate.contact"
                                                   class="text-teal-600 hover:underline text-sm flex items-center mb-2" target="_blank"
                                                >
                                                    {{ affiliate.contact }}
                                                    <icon class="ml-1" name="arrow-right-up"></icon>
                                                </a>
                                                <span v-else>{{ affiliate.contact }}</span>
                                            </div>
                                            <div>
                                                <label class="block mb-1 text-sm font-medium text-gray-700">
                                                    Website/Channel:
                                                </label>
                                                <a :href="`https://${affiliate.website}`" target="_blank" class="text-teal-600 hover:underline text-sm flex items-center mb-2">
                                                    {{ affiliate.website }}
                                                    <icon class="ml-1" name="arrow-right-up"></icon>
                                                </a>
                                            </div>
                                            <label class="block mb-1 text-sm font-medium text-gray-700">Suggested message:</label>
                                            <div
                                                class="w-full border rounded p-2 text-sm text-gray-800 whitespace-pre-line"
                                                readonly
                                            >
                                                {{ getSuggestedMessage(affiliate) }}
                                            </div>
                                        </div>
                                    </template>
                                </drawer>
                            </div>
                        </div>

                        <div class="text-sm text-gray-500 mb-2">
                            Audience: {{ affiliate.audience }}
                        </div>

                        <div class="flex items-center gap-2">
                            <div class="text-sm text-gray-600">Relevance:</div>
                            <div class="flex-1 bg-gray-200 rounded-full h-3 overflow-hidden">
                                <div class="bg-teal-500 h-full" :style="{ width: (affiliate.relevance_score * 10) + '%' }"></div>
                            </div>
                            <div class="text-sm text-gray-700 font-medium">{{ affiliate.relevance_score }}/10</div>
                        </div>
                    </div>
                </div>
                <div class="text-sm mb-10">
                    <p class="text-gray-500 mb-2">If you’d like to update your preferences or start over, simply click the Reset button below.</p>
                    <btn size="small" @click="resetData">Reset</btn>
                </div>
            </div>
        </div>

        <div class="bg-gray-50 px-10 py-4 text-right rounded-b sticky bottom-0 z-40" v-if="competitors.length > 0 && !discoveredAffiliates.length">
            <btn
                @click.prevent="discoverAffiliates"
                type="primary"
                :loading="discoveringAffiliates"
                :disabled="discoveringAffiliates"
            >
                {{ discoveringAffiliates ? 'Looking for affiliates...' : 'Discover' }}
            </btn>
        </div>

    </div>
</template>

